<template>
  <BaseDialog
    :open="open"
    :disabled="loadingStudents || loadingGroups"
    max-width="1360px"
    @close="onClose"
  >
    <template #title>
      <span>Rückmeldungsbogen drucken</span>
      <span v-if="template">
        — <strong>{{ template.topic || template.title || ('Vorlage #' + template.id) }}</strong>
        <template v-if="template.subject"> ({{ template.subject }})</template>
      </span>
    </template>

    <div class="dialog-scroll">
      <p
        v-if="!template"
        class="notice notice-danger"
        role="alert"
      >
        Keine Vorlage ausgewählt.
      </p>

      <div
        v-else
        class="form"
        style="display:grid;grid-template-columns:1fr 1fr;gap:12px;"
      >
        <!-- Verfügbare Schüler (Pool) -->
        <div class="field">
          <label for="flt-students">Verfügbare Schüler</label>
          <input
            id="flt-students"
            v-model="studentFilter"
            :disabled="loadingStudents"
            autocomplete="off"
            placeholder="Filtern (Name/Nummer)"
          >

          <div
            class="chip-box"
            style="margin-top:8px;"
          >
            <div
              class="chips"
              style="display:flex;flex-wrap:wrap;gap:6px;max-height:260px;overflow:auto;"
            >
              <button
                v-for="s in filteredPool"
                :key="s.id"
                type="button"
                class="chip"
                :disabled="loadingStudents"
                :title="studentLabel(s)"
                @click="addStudent(s.id)"
              >
                {{ studentLabel(s) }}
              </button>
              <p
                v-if="filteredPool.length===0"
                class="muted"
              >
                Keine Treffer.
              </p>
            </div>
          </div>

          <p class="help mt-4">
            Die Auswahl bestimmt die Anzahl der Rückmeldungsbögen im PDF.
          </p>

          <!-- Gruppen (Sammelzuweisung) -->
          <div class="field mt-4">
            <label for="flt-groups">Gruppen (Sammelzuweisung)</label>
            <input
              id="flt-groups"
              v-model="groupFilter"
              :disabled="loadingGroups"
              autocomplete="off"
              placeholder="Gruppen filtern"
            >
            <div
              class="chip-box"
              style="margin-top:8px;"
            >
              <div
                class="chips"
                style="display:flex;flex-wrap:wrap;gap:6px;max-height:180px;overflow:auto;"
              >
                <button
                  v-for="g in filteredGroups"
                  :key="g.id"
                  type="button"
                  :class="['chip', g.is_private ? 'warn' : '']"
                  :disabled="loadingGroups"
                  :title="groupLabel(g)"
                  @click="addGroup(g.id)"
                >
                  {{ groupLabel(g) }}
                </button>
                <p
                  v-if="loadingGroups"
                  class="muted"
                >
                  Gruppen werden geladen…
                </p>
                <p
                  v-else-if="filteredGroups.length===0"
                  class="muted"
                >
                  Keine Gruppen verfügbar.
                </p>
              </div>
            </div>
            <p
              v-if="visibleGroups.length"
              class="help"
            >
              Tipp: Klick auf eine Gruppe wählt alle Mitglieder aus.
            </p>
          </div>
        </div>

        <!-- Ausgewählte Schüler -->
        <div class="field">
          <label>Ausgewählte Schüler</label>
          <div
            class="chip-box"
            style="margin-top:8px;"
          >
            <div
              class="chips"
              style="display:flex;flex-wrap:wrap;gap:6px;max-height:460px;overflow:auto;"
            >
              <button
                v-for="s in selectedStudents"
                :key="s.id"
                type="button"
                class="chip active"
                :disabled="loadingStudents"
                :title="studentLabel(s)"
                @click="removeStudent(s.id)"
              >
                {{ studentLabel(s) }}
              </button>
              <p
                v-if="selectedStudents.length===0"
                class="muted"
              >
                Noch keine Schüler ausgewählt.
              </p>
            </div>
          </div>
        </div>
      </div>

      <p
        v-if="errorMsg"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ errorMsg }}
      </p>
    </div>

    <template #footer>
      <div style="display:flex;gap:8px;justify-content:flex-end;align-items:center;flex-wrap:wrap;">
        <button
          class="btn mr-6"
          type="button"
          :disabled="loadingStudents || loadingGroups"
          @click="onClose"
        >
          Schließen
        </button>

        <label
          class="meta"
          for="print-date"
        >Datum:</label>
        <flat-pickr
          id="print-date"
          v-model="assessedOn"
          :config="fpConfig"
          class="input"
          aria-label="Datum wählen"
          style="max-width:150px;"
        />

        <button
          class="btn btn-primary"
          type="button"
          :disabled="(loadingStudents || loadingGroups) || !template || selectedIds.size===0"
          @click="onPrint"
        >
          Drucken ({{ selectedIds.size }})
        </button>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {fetchActiveStudents} from '@/api/students';
import {exportFeedbackFormsPdf} from '@/pdf/feedbackFormsPdf';
import api from '@/services/api';

const props = defineProps({
  open: {type: Boolean, default: false},
  template: {type: Object, default: null},
});
const emit = defineEmits(['close']);

const loadingStudents = ref(false);
const loadingGroups = ref(false);

const errorMsg = ref('');
const studentFilter = ref('');

// Datum (ISO im Model), Anzeige DD.MM.YYYY
function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
const assessedOn = ref(todayIso());
const fpConfig = {
  altInput: true,
  altFormat: 'd.m.Y',
  dateFormat: 'Y-m-d',
  locale: 'de',
  allowInput: true
};

const allStudents = ref([]);
const selectedIds = ref(new Set());

const selectedStudents = computed(() => {
  const ids = selectedIds.value;
  return (allStudents.value || []).filter(s => ids.has(Number(s.id)));
});

const poolStudents = computed(() => {
  const ids = selectedIds.value;
  return (allStudents.value || []).filter(s => !ids.has(Number(s.id)));
});

const filteredPool = computed(() => {
  const q = studentFilter.value.trim().toLowerCase();
  if (!q) return poolStudents.value;
  return poolStudents.value.filter(s => studentLabel(s).toLowerCase().includes(q));
});

// Gruppen
const groupFilter = ref('');
const groups = ref([]);                 // alle Gruppen
const groupMembersMap = ref(new Map()); // Map<groupId, Set<studentId>>

const visibleGroups = computed(() => {
  return groups.value.filter(g => {
    const members = groupMembersMap.value.get(Number(g.id)) || new Set();
    if (members.size === 0) return false;
    for (const sid of members) {
      if (!selectedIds.value.has(Number(sid))) return true;
    }
    return false;
  });
});

const filteredGroups = computed(() => {
  const q = groupFilter.value.trim().toLowerCase();
  if (!q) return visibleGroups.value;
  return visibleGroups.value.filter(g => groupLabel(g).toLowerCase().includes(q));
});

async function loadStudents() {
  loadingStudents.value = true;
  errorMsg.value = '';
  try {
    const st = await fetchActiveStudents();
    allStudents.value = Array.isArray(st) ? st : [];
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Schüler konnten nicht geladen werden.';
    allStudents.value = [];
  } finally {
    loadingStudents.value = false;
  }
}

async function loadGroupsInBackground() {
  loadingGroups.value = true;
  try {
    const {data: gr} = await api.get('/groups', {params: {page: 1, limit: 500, withCounts: false}});
    groups.value = Array.isArray(gr?.items) ? gr.items : [];

    const memberPairs = await Promise.all(
        groups.value.map(async (g) => {
          try {
            const {data} = await api.get(`/groups/${g.id}/members`);
            const ids = new Set((Array.isArray(data?.items) ? data.items : []).map(s => Number(s.id)));
            return [Number(g.id), ids];
          } catch {
            return [Number(g.id), new Set()];
          }
        })
    );
    groupMembersMap.value = new Map(memberPairs);
  } catch (e) {
    // Gruppen sind optional: Fehler nicht hart blockieren
    groups.value = [];
    groupMembersMap.value = new Map();
  } finally {
    loadingGroups.value = false;
  }
}

function getPreselectedIdsFromTemplate(tpl) {
  if (!tpl || typeof tpl !== 'object') return [];

  // Priorität 1: explizite ID-Liste
  const rawIds = Array.isArray(tpl.preselected_student_ids) ? tpl.preselected_student_ids : null;
  if (rawIds) {
    return rawIds.map(n => Number(n)).filter(n => Number.isFinite(n) && n > 0);
  }

  // Priorität 2: mitgegebene Schüler-Objekte (z. B. aus Lernbrief)
  const rawStudents = Array.isArray(tpl.students) ? tpl.students : [];
  return rawStudents
      .map(s => Number(s?.id))
      .filter(n => Number.isFinite(n) && n > 0);
}

function studentLabel(s) {
  const num = s.student_number ? ` · ${s.student_number}` : '';
  return `${s.last_name}, ${s.first_name}${num}`;
}

function groupLabel(g) {
  const count = (groupMembersMap.value.get(Number(g.id)) || new Set()).size;
  const base = `${g.name}${count ? ' (' + count + ')' : ''}`;
  const priv = g.is_private ? ' - privat' : '';
  return `${base}${priv}`;
}

function addGroup(groupId) {
  const gid = Number(groupId);
  const members = groupMembersMap.value.get(gid) || new Set();
  if (members.size === 0) return;
  const s = new Set(selectedIds.value);
  members.forEach(id => s.add(Number(id)));
  selectedIds.value = s;
}

function addStudent(id) {
  const s = new Set(selectedIds.value);
  s.add(Number(id));
  selectedIds.value = s;
}

function removeStudent(id) {
  const s = new Set(selectedIds.value);
  s.delete(Number(id));
  selectedIds.value = s;
}

function onClose() {
  emit('close');
}

async function onPrint() {
  if (!props.template) return;

  const students = (allStudents.value || []).filter(s => selectedIds.value.has(Number(s.id)));
  if (students.length === 0) return;

  errorMsg.value = '';
  try {
    await exportFeedbackFormsPdf({template: props.template, students, date: assessedOn.value});
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'PDF konnte nicht erstellt werden.';
  } finally {
    // nix
  }
}

watch(() => props.open, (val) => {
  if (val) {
    studentFilter.value = '';
    groupFilter.value = '';

    const pre = getPreselectedIdsFromTemplate(props.template);
    selectedIds.value = new Set(pre);

    // Schüler zuerst -> UI sofort nutzbar
    loadStudents();

    // Gruppen/Members parallel im Hintergrund
    loadGroupsInBackground();
  } else {
    errorMsg.value = '';
    allStudents.value = [];
    groups.value = [];
    groupMembersMap.value = new Map();
  }
});
</script>

<style scoped>
.chip-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.4;
}

.chip.active {
  background: rgba(79, 70, 229, 0.12);
  border-color: var(--primary);
  color: var(--primary-600);
}

.chip.warn {
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.5);
  color: var(--warn);
}

.muted {
  color: var(--muted);
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
}

.dialog-scroll {
  max-height: 80vh;
  overflow: auto;
}

:deep(.dialog-footer) .input {
  width: 100px;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px;
  background: var(--surface);
  height: 36px;
  text-align: center;
}

.input.error {
  border-color: color-mix(in srgb, var(--danger) 60%, var(--border));
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--danger) 22%, transparent);
}
</style>
